<script lang="ts">
	import { createTippy } from 'svelte-tippy'
	import type { Instance } from 'tippy.js'
	import 'tippy.js/dist/tippy.css'
	import 'tippy.js/themes/light.css'
	import 'tippy.js/animations/shift-away-subtle.css'
	import 'tippy.js/animations/scale-extreme.css'
	import 'tippy.js/animations/shift-toward-extreme.css'
	import 'tippy.js/dist/svg-arrow.css'
	import Icon from './Icon.svelte'
	import type { Snippet } from 'svelte'
	import { onDestroy } from 'svelte'
	import { TooltipPlacements } from '$types/enums'
	import infoIcon from '@/icons/icon_info_blue_outline_24px.svg'

	interface Props {
		msg?: string
		ttipIconImage?: string
		ttipIconWidth?: number
		ttipIconHeight?: number
		delayTtip?: [number, number]
		placementTtip?: TooltipPlacements
		themeTtip?: string
		maxWidthTtip?: number | string
		animationTTip?: string | boolean
		offsetTtip?: [number, number] | null
		showOnCreate?: boolean
		keepOnClick?: boolean // when clicking on the tooltip, it keeps showing until clicking again
		isHtml?: boolean
		appendToObject?: Element | 'parent' | ((ref: Element) => Element) | undefined
		removeCloseIconAfterClose?: boolean
		children?: Snippet
		content?: Snippet
		zIndex?: number
		ref?: HTMLElement
	}

	let {
		msg = '',
		ttipIconImage = infoIcon,
		ttipIconWidth = 24,
		ttipIconHeight = 24,
		delayTtip = [100, 200],
		placementTtip = TooltipPlacements.RIGHT,
		themeTtip = 'mmttip',
		maxWidthTtip = 367,
		animationTTip = 'scale-extreme',
		offsetTtip = null,
		showOnCreate = $bindable(false),
		keepOnClick = false,
		isHtml = true,
		appendToObject = document.body,
		removeCloseIconAfterClose = false,
		children,
		content,
		zIndex = 9999,
		ref = $bindable()
	}: Props = $props()

	export function show(withCloseButton = false) {
		if (tippyInstance) {
			showOnCreate = withCloseButton
			tippyInstance.show()
		}
	}

	export function hide() {
		if (tippyInstance) {
			tippyInstance.hide()
		}
	}

	export function enable() {
		if (tippyInstance) {
			tippyInstance.enable()
		}
	}

	export function disable() {
		if (tippyInstance) {
			tippyInstance.disable()
		}
	}

	let contentMsg: string = $derived(`<div>${msg}</div>`)
	let contentSlotContainer: HTMLElement | undefined = $state()
	let closeButton: HTMLElement | undefined = $state()

	let tippyInstance: Instance
	let isPinnedByClick = false
	let removeReferenceClickListener: (() => void) | null = null

	function getOffsetTtip(): [number, number] {
		// dividing by 4 shifts the arrow 10px, that's why this value is used here. If the standard icon size is kept
		// the offset remains the same. However, if the icon size changes, we must use a custom offset
		// To center the arrow when using right/left, try to use some offset like [0, x]
		if (
			placementTtip === TooltipPlacements.BOTTOM_START ||
			placementTtip === TooltipPlacements.TOP_START
		) {
			return [-ttipIconWidth / 4, 10]
		}

		if (
			placementTtip === TooltipPlacements.BOTTOM_END ||
			placementTtip === TooltipPlacements.TOP_END
		) {
			return [ttipIconWidth / 4, 10]
		}
		return [-ttipIconWidth / 4, 10]
	}

	const tippy = createTippy({
		allowHTML: isHtml,
		zIndex: zIndex,
		interactive: true,
		appendTo: appendToObject,
		delay: delayTtip,
		placement: placementTtip,
		animation: animationTTip,
		theme: themeTtip,
		maxWidth: maxWidthTtip,
		offset: offsetTtip ? offsetTtip : getOffsetTtip(),
		showOnCreate: showOnCreate,
		onCreate(instance: Instance) {
			tippyInstance = instance

			const onReferenceClick = (event: Event) => {
				if (showOnCreate) {
					return
				}

				event.preventDefault()
				event.stopPropagation()

				if (isPinnedByClick) {
					isPinnedByClick = false
					instance.setProps({
						trigger: 'mouseenter focus click'
					})
					instance.hide()
					return
				}

				isPinnedByClick = true
				instance.show()
				instance.setProps({
					trigger: 'manual'
				})
			}

			if (keepOnClick) {
				instance.reference.addEventListener('click', onReferenceClick)
				window.addEventListener('click', (e) => {
					if (
						isPinnedByClick &&
						e.target instanceof Element &&
						!e.target.closest('.tippy-content')
					) {
						onReferenceClick(e)
					}
				})
				removeReferenceClickListener = () => {
					instance.reference.removeEventListener('click', onReferenceClick)
					window.removeEventListener('click', (e) => {
						if (
							isPinnedByClick &&
							e.target instanceof Element &&
							!e.target.closest('.tippy-content')
						) {
							onReferenceClick(e)
						}
					})
				}
			}
		},
		onShown(instance: Instance) {
			if (showOnCreate) {
				if (closeButton) {
					let parent = closeButton.parentElement
					if (parent) {
						parent.style.flexDirection = 'row !important'
					}
					closeButton.addEventListener('click', () => {
						instance.hide()
					})
					instance.popper.querySelector('.tippy-content')?.appendChild(closeButton)
					closeButton.style.display = 'block'
				}
				instance.setProps({
					hideOnClick: false,
					trigger: 'manual'
				})
				return instance
			}
		},
		onHide() {
			if (!showOnCreate && isPinnedByClick) {
				return false
			}

			// hidden instead of removed: the node is bound, so Svelte still owns it
			if (showOnCreate && removeCloseIconAfterClose && closeButton) {
				closeButton.style.display = 'none'
			}
		},
		onHidden(instance: Instance) {
			if (showOnCreate) {
				// If there is a content snippet, do not overwrite the content with the (probably empty) msg.
				if (!content) {
					instance.setContent(`<div>${msg}</div>`) //must be nested in div, because otherwise the content is displayed in rows
				}
				instance.setProps({
					allowHTML: isHtml,
					delay: [100, 200],
					hideOnClick: true,
					trigger: 'mouseenter focus'
				})
				showOnCreate = false
				return instance
			}
		}
	})

	onDestroy(() => {
		if (removeReferenceClickListener) {
			removeReferenceClickListener()
			removeReferenceClickListener = null
		}

		if (tippyInstance && !tippyInstance.state.isDestroyed) {
			tippyInstance.destroy()
		}
	})
</script>

{#if content}
	<div use:tippy={{ content: contentSlotContainer }} bind:this={ref}>
		{@render children?.()}
	</div>
	<div bind:this={contentSlotContainer}>
		{@render content?.()}
	</div>
{:else if children}
	<div class="tooltip-container-slot" use:tippy={{ content: contentMsg }} bind:this={ref}>
		{@render children?.()}
	</div>
{:else}
	<div
		class="tooltip-info-icon-container"
		class:showOnCreate
		use:tippy={{ content: contentMsg }}
		bind:this={ref}
	>
		<Icon
			width={ttipIconWidth}
			height={ttipIconHeight}
			src={ttipIconImage}
			allowTransition={false}
		/>
	</div>
{/if}

<!-- moved into this instance's popper by onShown; must stay instance-owned, ids would collide -->
<div bind:this={closeButton} class="close-button" style="display: none"></div>

<style lang="scss">
	@use '$styles/global';

	.tooltip-info-icon-container {
		@include global.flex-row;

		width: auto;
		height: auto;
	}

	:global(.tippy-box[data-theme='mmttip']) {
		@include global.roboto-font(1.125rem, 400, 0.75rem, black);

		filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.25));
		background-color: #fff;
		border-radius: 2px;
		max-height: 47px;
		max-width: 260px;
		min-height: 35px;
		display: table-cell;
		vertical-align: middle;
	}

	:global(.tippy-box[data-theme~='mmttip'] > .tippy-content) {
		padding: 10px 12px 10px;
		display: flex;
		column-gap: 2px;
		line-height: 1.125rem;
	}

	:global(.tippy-box[data-theme~='mmttip'] > .tippy-content > div) {
		@extend .scrollable;

		max-height: 18.75rem;
		overflow-y: auto;
	}

	:global(.tippy-box[data-theme~='mmttip'] > .tippy-arrow::before) {
		color: #fff;
		border-radius: 4px;
	}

	.close-button {
		width: 16px;
		height: 20px;
		min-width: 16px;
		cursor: pointer;
		background-size: contain;
		background-image: url('@/icons/icon_cancel_tt.svg');
		background-repeat: no-repeat;
		margin-right: -6px;
		margin-top: -5px; // must be shifted upwards, as margin on top should be only 5px
	}

	.close-button:hover {
		filter: brightness(0%);
	}

	.close-button:not(:hover) {
		filter: invert(85%) sepia(0%) saturate(18%) hue-rotate(47deg) brightness(95%) contrast(89%);
	}
</style>
